import type {
  Candidate,
  Configuration,
  Configuration$Options,
  Configuration$Slice,
  Device,
  Device$Audio,
  Device$Control,
  Device$Video,
  Minimist,
  Status
} from './types.js';
import type { OptionalDeep } from '@studio/js-utils';
import { toAudio, toUri as toAudioUri } from '@studio/models/audio';
import { toControl, toUri as toControlUri } from '@studio/models/control';
import { DefaultModelId, DeviceDefaults } from '@studio/models/device';
import type { Authentication } from '@studio/models/livekit';
import { formatUri } from '@studio/models/net';
import { toVideo, toUri as toVideoUri } from '@studio/models/video';
import { isNotEmpty, isNotNil, transformArray } from '@tryiris-ai/js-types';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { defaults, merge, pick } from 'lodash-es';
import { formatQuery } from '@studio/models/net';
import { isDisplayInversionEnabled } from '#~/posthog/manager.js';

/**
 * Determines if the specified device is an audio device.
 *
 * @param device - Device to be queried.
 * @returns `true` if the specified device is an audio device, otherwise `false`.
 */
export const isAudioDevice = (device?: Device): device is Device$Audio => isNotNil(device?.audio);

/**
 * Determines if the specified device is a control device.
 *
 * @param device - Device to be queried.
 * @returns `true` if the specified device is a control device, otherwise `false`.
 */
export const isControlDevice = (device?: Device): device is Device$Control =>
  isNotNil(device?.control);

/**
 * Determines if the specified device is a video device.
 *
 * @param device - Device to be queried.
 * @returns `true` if the specified device is a video device, otherwise `false`.
 */
export const isVideoDevice = (device?: Device): device is Device$Video => isNotNil(device?.video);

/**
 * Extract the candidate subset of the specified device.
 *
 * @param device - Device.
 * @returns Candidate subset.
 */
export const toCandidate = (device: Device): Candidate => {
  const { audio, control, video, ...rest } = device;

  return merge(
    { ...rest },
    audio ? { audio: { offset: audio.offset, ...audio.config } } : undefined,
    control ? { control: control.config } : undefined,
    video ? { video: video.config } : undefined
  );
};

/**
 * Extracts the configuration options from the specified device data.
 *
 * @param config - Device configuration.
 * @param options - Configuration options-options. Naming things is hard.
 * @returns Configuration options or `undefined`
 */
export const toConfigOptions = (
  config: T_Maybe<OptionalDeep<Configuration>>,
  options: T_Optional<Pick<Device, 'id' | 'name'>> = {}
): T_Maybe<Configuration$Options> => {
  if (!config) {
    return undefined;
  }

  const { audio, control, video } = config;

  /*
   * The opts URI is of the form: `opts://user:password@<device-name>`.  Run through the URI for each aspect and
   * determine if there is anything we need, specifically, the username and password.  If we find anything, then
   * we'll add it to the opts URI.
   */
  const datum = [audio, control, video].reduce<object>((obj, µ) => {
    if (µ && 'credentials' in µ) {
      const { password, username } = µ.credentials ?? {};

      return username || password ? { ...obj, password, username } : obj;
    }

    return obj;
  }, {});

  return isNotEmpty(datum)
    ? { host: options.name ?? options.id ?? DeviceDefaults.name, protocol: 'opts', ...datum }
    : undefined;
};

/**
 * Extracts the aggregated configuration from the specified device.
 *
 * @param device - Device.
 * @returns Configuration sub-object.
 */
export function toConfiguration(device: T_Maybe<T_Optional<Configuration$Slice>>): Configuration {
  return {
    audio: device?.audio?.config,
    control: device?.control?.config,
    video: device?.video?.config
  };
}

/**
 * Converts a candidate to a fully defined device.
 *
 * @param candidate - Device candidate.
 * @returns Fully hydrated device.
 */
export const toDevice = (candidate: Minimist): Device => {
  const { audio, control, video, ...rest } = candidate;

  return merge(
    defaults(rest, DeviceDefaults),
    audio ? { audio: toAudio(audio) } : undefined,
    control ? { control: toControl(control) } : undefined,
    video ? { video: toVideo(video) } : undefined
  );
};

/**
 * Extracts the aggregated status from the specified device.
 *
 * @param device - Device.
 * @returns The status values.
 */
export const toStatus = (device: T_Maybe<Device & { pending?: boolean }>): Status => {
  const { audio, control, video } = device ?? {};

  const { calibration, movement } = control ?? {};

  const hasMovement = isNotEmpty(control?.movement);
  const hasCalibration = isNotEmpty(calibration);

  return {
    audio: isNotEmpty(audio) ? pick(audio, 'status') : undefined,
    control:
      hasMovement || hasCalibration
        ? {
            calibration: hasCalibration ? pick(calibration, ['progress', 'status']) : undefined,
            movement: hasMovement ? pick(movement, 'status') : undefined
          }
        : undefined,
    device,
    powerState: device?.powerState,
    video: isNotEmpty(video) ? pick(video, ['network', 'status']) : undefined
  };
};

/**
 * Generate the URX for the specified device.
 *
 * @param config - Aggregated device configuration.
 * @param options - Additional options.
 * @returns The URX or `undefined`.
 */
export const toUrx = (
  config: OptionalDeep<Configuration>,
  options?: T_Optional<Pick<Device, 'modelId'> & { isTransient: boolean; liveKit: Authentication }>
): T_Maybe<string> => {
  const { audio, control, video } = config;
  const { isTransient, liveKit, modelId = DefaultModelId } = options ?? {};

  // Opt-in gate (PostHog `enable-display-inversion`, off by default): only when
  // on do we tell the encoder to run its inverted-display correction, via the
  // ptzcontroller opts URX. (Sync/fail-safe read lives in the helper.)
  const enableDisplayInvert = isDisplayInversionEnabled();
  const ptzQuery = `pan=true&tilt=true&zoom=false&modelId=${modelId}${
    enableDisplayInvert ? '&enableDisplayInvert=true' : ''
  }`;

  const audioUri = audio ? toAudioUri(audio, { liveKit }) : undefined;
  const controlUri = control ? toControlUri(control, { modelId }) : undefined;
  const videoUri = video ? toVideoUri(video, { liveKit }) : undefined;

  // Concatenate the URI for each aspect.
  const urx = transformArray(
    [
      ...(!isTransient && (videoUri || audioUri)
        ? [
            {
              host: 'default',
              protocol: 'egress',
              query: formatQuery(liveKit)
            }
          ]
        : []),
      ...(controlUri
        ? !isTransient
          ? [
              {
                host: 'default',
                protocol: 'autotracking'
              },
              {
                host: 'tracking',
                /* cspell:disable-next-line */
                protocol: 'ptzcontroller',
                query: ptzQuery
              },
              {
                host: 'default',
                protocol: 'calibration'
              }
            ]
          : [
              {
                host: 'tracking',
                /* cspell:disable-next-line */
                protocol: 'ptzcontroller',
                query: ptzQuery
              }
            ]
        : []),
      videoUri,
      audioUri,
      controlUri
    ],
    {
      transformer: formatUri
    }
  );

  return urx.length ? urx.join(',') : undefined;
};
